import { createSlice } from "@reduxjs/toolkit";
import { fetchAppPermissions } from "../actions/permissionsActions";
import {
  LITE_LOCKED_PERMISSION_SLUGS,
  MEMBER_PLAN,
  planFromUserType,
  planRank,
} from "../../utils/memberPlan";

export const PERMISSION_LEVEL = {
  BLOCKED: "blocked",
  LIMITED: "limited",
  POPUP: "popup",
  COMPLETE: "complete",
};

// Window during which a client-side optimistic permissions flip (set right
// after a successful RC purchase or restore) shadows any "free-tier"
// payload returned by /api/app/permissions/list. Matches the TTL used for
// the user-slice's `markPremiumOptimistic` (userSlice.js:28
// OPTIMISTIC_TTL_MS) so the two stay in lockstep — both shields drop at
// the same point, leaving a single coherent "we waited long enough,
// accept the canonical truth" boundary.
const OPTIMISTIC_TTL_MS = 10 * 60 * 1000;

const isOptimisticActive = (state) => {
  if (!state._optimistic) return false;
  const startedAt = Date.parse(state._optimisticAt || 0);
  return Number.isFinite(startedAt) && Date.now() - startedAt < OPTIMISTIC_TTL_MS;
};

// Walk every cached section/action and force its level to COMPLETE so
// every `useAppPermission(slug).level` read returns COMPLETE immediately
// after a successful purchase. Preserves the section/action shape so
// downstream consumers (popup_text, name, sectionSlug) keep working.
// Premium Lite: the actions Lite doesn't include stay POPUP until the backend
// answers with the CRM's real Lite levels.
const upgradeActionsForPlan = (sections, targetPlan) =>
  (sections || []).map((section) => ({
    ...section,
    actions: (section.actions || []).map((action) => {
      const slug = `${section.slug}.${action.slug}`;
      const liteLocked =
        targetPlan === MEMBER_PLAN.PREMIUM_LITE &&
        (LITE_LOCKED_PERMISSION_SLUGS.includes(slug) ||
          LITE_LOCKED_PERMISSION_SLUGS.includes(action.slug));
      return {
        ...action,
        level: liteLocked ? PERMISSION_LEVEL.POPUP : PERMISSION_LEVEL.COMPLETE,
      };
    }),
  }));

// The backend's plan, or one derived from user_type on older backends.
const planOf = (payload) =>
  payload?.plan ?? planFromUserType(payload?.user_type ?? null);

const buildActionsMap = (sections = []) => {
  const map = {};
  for (const section of sections) {
    for (const action of section.actions || []) {
      map[action.slug] = {
        slug: action.slug,
        name: action.name,
        level: action.level,
        popup_text: action.popup_text,
        // Premium Lite (client, 20/10/2026): popups have a title; the backend
        // already resolves the Lite copy for a Lite member.
        popup_title: action.popup_title ?? null,
        sectionSlug: section.slug,
      };
    }
  }
  return map;
};

const initialState = {
  userType: null,
  // guest | free | premium_lite | premium (see utils/memberPlan).
  plan: null,
  sections: [],
  popupActions: [],
  actionsBySlug: {},
  isLoading: false,
  isLoaded: false,
  error: null,
  // Set by `markPremiumPermissionsOptimistic` after a successful RC
  // purchase or restore. While truthy AND inside the TTL, incoming
  // non-premium responses from /api/app/permissions/list are ignored —
  // see the `fetchAppPermissions.fulfilled` reducer below.
  _optimistic: false,
  _optimisticAt: null,
  // The plan just bought: answers below it don't replace the optimistic state
  // (e.g. a stale Premium Lite answer right after upgrading to Premium).
  _optimisticPlan: null,
};

const permissionsSlice = createSlice({
  name: "permissions",
  initialState,
  reducers: {
    clearPermissions: () => ({ ...initialState }),
    // Called from the Paywall right after RevenueCat's local SDK confirms
    // a successful purchase / restore. Flips every cached action level to
    // COMPLETE so the Menu / Meal Plan / etc. content unblocks
    // immediately — without this, gates stay at their pre-purchase
    // POPUP/BLOCKED until the next cold start because the RC → backend
    // webhook is async and `/api/app/permissions/list` would still
    // resolve to "free" if queried right away.
    //
    // Payload (optional): { targetPlan: "premium" | "premium_lite" }, the
    // plan bought; defaults to full Premium.
    markPremiumPermissionsOptimistic: (state, action) => {
      const targetPlan =
        action?.payload?.targetPlan === MEMBER_PLAN.PREMIUM_LITE
          ? MEMBER_PLAN.PREMIUM_LITE
          : MEMBER_PLAN.PREMIUM;
      const upgradedSections = upgradeActionsForPlan(state.sections, targetPlan);
      state.sections = upgradedSections;
      state.actionsBySlug = buildActionsMap(upgradedSections);
      state.userType = "premium";
      state.plan = targetPlan;
      // Full Premium has no popup-gated actions; Lite keeps the buttons
      // ("Passa a Premium") for the actions it doesn't include.
      if (targetPlan === MEMBER_PLAN.PREMIUM) state.popupActions = [];
      state._optimistic = true;
      state._optimisticAt = new Date().toISOString();
      state._optimisticPlan = targetPlan;
      state.isLoaded = true; // unblock callers that wait on isLoaded
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAppPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.error = null;

        const incomingUserType = action.payload?.user_type ?? null;
        const incomingPlan = planOf(action.payload);
        const incomingSections = action.payload?.sections ?? [];
        const incomingPopups = Array.isArray(action.payload?.popup_actions)
          ? action.payload.popup_actions
          : [];

        // Inside the post-purchase optimistic window, an answer below the
        // plan just bought is a backend that hasn't caught up yet (the RC /
        // Stripe webhook is still in flight): keep the upgraded local state.
        // That covers "still free" after a first purchase AND "still Premium
        // Lite" after an upgrade to Premium. Past the TTL, or never
        // optimistic, the canonical answer wins — if the window expired
        // without confirmation something went wrong (refund, RC delivery
        // failure) and we'd rather show the truth than a stale flag.
        if (
          isOptimisticActive(state) &&
          planRank(incomingPlan) < planRank(state._optimisticPlan ?? MEMBER_PLAN.PREMIUM)
        ) {
          return;
        }

        state.userType = incomingUserType;
        // Only the backend's own `plan`: without it (an older backend) the
        // plan selector falls back to the profile's access_info.plan, which
        // tells Premium Lite from Premium; user_type alone cannot.
        state.plan = action.payload?.plan ?? null;
        state.sections = incomingSections;
        state.popupActions = incomingPopups;
        state.actionsBySlug = buildActionsMap(incomingSections);
        state._optimistic = false;
        state._optimisticAt = null;
        state._optimisticPlan = null;
      })
      .addCase(fetchAppPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoaded = true;
        state.error = action.payload || action.error?.message || null;
      });
  },
});

export const { clearPermissions, markPremiumPermissionsOptimistic } =
  permissionsSlice.actions;
export default permissionsSlice.reducer;
